/*  src/components/SystemOrderGeneratorModal.js
 *  ─────────────────────────────────────────────────────────────
 *  Modal do zarządzania zleceniami stałymi („standing_orders”)
 *  – dodawanie, edycja, lista oraz ⚡ Wymuszenie generowania.
 *  ───────────────────────────────────────────────────────────── */
import React, { useEffect, useState } from "react";
import "./SystemOrderGeneratorModal.css";
import { API_URL } from "../config"; // ✅ Krok 1: jedno źródło API (z /api na końcu)

/* tłumaczenie scheme → PL (używane w tabeli) */
const SCHEME_LABELS = {
  monthly:    "Miesięczne",
  quarterly:  "Kwartalne",
  halfyear:   "Półroczne",
  yearly:     "Roczne",
  everyNdays: "Co N dni",
  custom:     "Niestandardowe",
};

/* helper do ISO-stringa */
const iso = (d) => (d ? String(d).slice(0, 10) : "");

/* domyślny formularz */
const EMPTY_FORM = {
  base_title:      "",
  opis_zlecenia:   "",
  rodzaj_zgloszenia: "Przegląd A",
  waga:            "Neutralny",
  klient_id:       "",
  osoba_zlecajaca: "",
  scheme:          "monthly",
  day_of_month:    1,
  starting_month:  1,
  every_n_days:    30,
  custom_dates:    [],
  start_date:      "",
  end_date:        "",
};

/* ───────────────────────────────────────────────────────────── */

export default function SystemOrderGeneratorModal({ visible, onClose, onSaved })
{
  /* ---------- widok, lista, statusy ---------- */
  const [view,   setView]   = useState("menu");     // menu | list | form
  const [tab,    setTab]    = useState("active");   // active | inactive
  const [soList, setSoList] = useState([]);

  const [editingId, setEditingId] = useState(null);
  const [loading,   setLoading  ] = useState(false);
  const [listErr,   setListErr  ] = useState("");
  const [runMsg,    setRunMsg   ] = useState("");   // info z „Wymuś”

  /* ---------- formularz ---------- */
  const [form,        setForm]       = useState(EMPTY_FORM);
  const [error,       setError]      = useState("");
  const [clients,     setClients]    = useState([]);
  const [clientsErr,  setClientsErr] = useState("");
  const [customDateInput, setCustomDateInput] = useState("");

  /* ========== POBIERANIE LISTY SO ========== */
  useEffect(() => {
    if (view !== "list") return;
    (async () => {
      try {
        setLoading(true);
        const r = await fetch(`${API_URL}/standing-orders`, { credentials:"include" }); // ✅ bez /api
        if (!r.ok) throw new Error("Błąd pobierania listy");
        const data = await r.json();
        setSoList(Array.isArray(data) ? data : []);
      } catch (e) { setListErr(e.message); }
      finally     { setLoading(false);     }
    })();
  }, [view]);

  /* ========== POBIERANIE KLIENTÓW (przy wejściu do FORM) ========== */
  useEffect(() => {
    if (view !== "form") return;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/clients`, { credentials:"include" }); // ✅ bez /api
        if (!r.ok) throw new Error("Błąd pobierania klientów");
        const data = await r.json();
        setClients(Array.isArray(data) ? data : []);
      } catch (e) { setClientsErr(e.message); }
    })();
  }, [view]);

  /* ========== HANDLERY FORMULARZA ========== */
  const handleChange = ({ target:{ name, value } }) =>
    setForm(p => ({ ...p, [name]: value }));

  const handleSchemeChange = (e) => {
    const scheme = e.target.value;
    setForm(p => ({
      ...p,
      scheme,
      day_of_month: 1,
      starting_month: 1,
      every_n_days: 30,
      custom_dates: [],
    }));
  };

  const onStartDateChange = (e) => {
    const val = e.target.value;          // YYYY-MM-DD
    if (!val) return setForm(p => ({ ...p, start_date:"" }));
    const [, m, d] = val.split("-").map(Number);
    if (d > 28) return alert("Dzień miesiąca może być tylko 1-28.");
    setForm(p => ({
      ...p,
      start_date: val,
      day_of_month: d,
      starting_month: m,
    }));
  };

  /* --- custom dates --- */
  const addCustomDate = () => {
    if (!customDateInput) return;
    setForm(p => ({
      ...p,
      custom_dates: Array.from(new Set([...p.custom_dates, customDateInput])),
    }));
    setCustomDateInput("");
  };
  const removeCustomDate = d =>
    setForm(p => ({ ...p, custom_dates: p.custom_dates.filter(x => x!==d) }));

  /* ========== EDYCJA ========== */
  const startEdit = (rec) => {
    setEditingId(rec.id);
    setForm({
      ...EMPTY_FORM,
      ...rec,
      klient_id:    rec.klient_id    || "",
      custom_dates: rec.custom_dates || [],
      start_date:   iso(rec.start_date),
      end_date:     iso(rec.end_date),
    });
    setView("form");
  };

  /* ========== USUWANIE ========== */
  const handleDelete = async (id) => {
    const pwd = window.prompt("Podaj swoje hasło, aby usunąć to zlecenie:");
    if (!pwd) return;
    try {
      const r  = await fetch(`${API_URL}/standing-orders/${id}`, { // ✅ bez /api
        method:"DELETE",
        credentials:"include",
        headers:{ "Content-Type":"application/json" },
        body:JSON.stringify({ password: pwd })
      });
      const ct = r.headers.get("content-type") || "";
      const payload = ct.includes("json") ? await r.json() : await r.text();
      if (!r.ok) throw new Error(payload.error || payload || "Błąd usuwania");
      setSoList(prev => prev.filter(x => x.id !== id));
      alert("Usunięto");
    } catch (e){ alert(e.message); }
  };

  /* ========== ZAPIS (add / edit) ========== */
  const handleSubmit = async (e) => {
    e.preventDefault(); setError("");
    const required = ["base_title","rodzaj_zgloszenia","waga","scheme","start_date","end_date"];
    if (required.some(k => !form[k]))
      return setError("Uzupełnij wszystkie wymagane pola.");
    if (form.scheme==="custom" && form.custom_dates.length===0)
      return setError("Dodaj przynajmniej jedną datę w trybie „Niestandardowe”.");

    const url    = editingId
      ? `${API_URL}/standing-orders/${editingId}` // ✅ bez /api
      : `${API_URL}/standing-orders`;
    const method = editingId ? "PUT" : "POST";

    try{
      const r  = await fetch(url, {
        method, credentials:"include",
        headers:{ "Content-Type":"application/json" },
        body:JSON.stringify({ ...form, klient_id: form.klient_id || null })
      });
      const ct = r.headers.get("content-type") || "";
      const payload = ct.includes("json") ? await r.json() : await r.text();
      if (!r.ok) throw new Error(payload.error || payload || "Błąd zapisu");
      /* odświeżamy listę */
      setEditingId(null); setView("list"); onSaved?.();
    }catch(err){ setError(err.message); }
  };

  /* ========== RESET FORMULARZA ========== */
  const openEmptyForm = () => {
    setEditingId(null); setForm(EMPTY_FORM); setCustomDateInput(""); setView("form");
  };

  /* ==========  ⚡ WYMUSZ GENEROWANIE ========== */
  const handleForceRun = async () => {
    setRunMsg("");
    try{
      const r = await fetch(`${API_URL}/standing-orders/force-run`,{ // ✅ bez /api
        method:"POST", credentials:"include"
      });
      const payload = await r.json();
      if(!r.ok) throw new Error(payload.error || "Błąd generowania");

      /* refresh listy */
      setView("menu"); setTimeout(()=>setView("list"),0);

      setRunMsg(`Sprawdzono ${payload.checked} zleceń – utworzono ${payload.generated}.`);
    }catch(e){ setRunMsg(e.message); }
  };

  /* ──────────────────────────────────────────────────────────
   *  JSX – trzy pod-widoki
   * ────────────────────────────────────────────────────────── */
  const content = (() => {

    /* ===== MENU ===== */
    if(view==="menu"){
      return(
        <div className="system-menu">
          <h3>Zlecenia Systemowe</h3>
          <button className="btn-submit big" onClick={openEmptyForm}>➕ Dodaj Nowe Zlecenie</button>
          <button className="btn-submit big" onClick={()=>setView("list")}>📄 Lista Zleceń</button>
          <button className="btn-submit" onClick={onClose}>Zamknij</button>
        </div>
      );
    }

    /* ===== LISTA ===== */
    if(view==="list"){
      return(
        <>
          <div className="list-header">
            <h3>Lista zleceń systemowych</h3>

            <div className="tabbar">
              <button className={tab==="active"   ? "active":""}
                      onClick={()=>setTab("active")}>Aktywne</button>
              <button className={tab==="inactive" ? "active":""}
                      onClick={()=>setTab("inactive")}>Nieaktywne</button>
            </div>

            {/* ⚡ wymuś */}
            <button className="btn-lg blue" onClick={handleForceRun}>
              ⚡ Wymuś&nbsp;Generowanie
            </button>
          </div>

          {loading && <p>Ładowanie…</p>}
          {listErr && <p className="error">{listErr}</p>}
          {runMsg  && <p className="info">{runMsg}</p>}

          {!loading && !listErr && (
            <table className="so-table">
              <thead>
                <tr>
                  <th>ID</th><th>Tytuł</th><th>Schemat</th><th>Okres</th><th className="center"></th>
                </tr>
              </thead>
              <tbody>
                {soList
                  .filter(r => tab==="active" ? r.stan_generowania : !r.stan_generowania)
                  .map(r => (
                    <tr key={r.id}>
                      <td>{r.id}</td>
                      <td>{r.base_title}</td>
                      <td>{SCHEME_LABELS[r.scheme] || r.scheme}</td>
                      <td>{iso(r.start_date)} → {iso(r.end_date)}</td>
                      <td className="actions center">
                        <button className="btn-table blue"
                                onClick={() => startEdit(r)}>Edytuj</button>
                        <button className="btn-table red"
                                onClick={() => handleDelete(r.id)}>Usuń</button>
                      </td>
                    </tr>
                  ))}
                {soList.filter(r=>tab==="active"?r.stan_generowania:!r.stan_generowania).length===0 && (
                  <tr><td colSpan="5" style={{textAlign:"center"}}>Brak danych</td></tr>
                )}
              </tbody>
            </table>
          )}

          {/* stopka – prawe dolne rogi */}
          <div className="footer-fixed">
            <button className="btn-lg blue"  onClick={() => setView("menu")}>⟵ Wróć</button>
            <button className="btn-lg grey"  onClick={onClose}>Zamknij</button>
          </div>
        </>
      );
    }

    /* ===== FORM (add / edit) ===== */
    return (
      <>
        <h3>{editingId ? "Edytuj zlecenie systemowe" : "Dodaj nowe zlecenie systemowe"}</h3>

        <form className="system-order-form" onSubmit={handleSubmit}>

          {/* ——— LEWA kolumna ——— */}
          <div className="column">
            <label>Tytuł *</label>
            <input type="text" name="base_title" value={form.base_title} onChange={handleChange} required />

            <label>Opis</label>
            <textarea name="opis_zlecenia" rows={5}
                      value={form.opis_zlecenia} onChange={handleChange} />

            <label>Rodzaj *</label>
            <select name="rodzaj_zgloszenia" value={form.rodzaj_zgloszenia} onChange={handleChange}>
              <option>Przegląd A</option><option>Przegląd Z</option><option>Wewnętrzne</option><option>Usługa</option>
            </select>

            <label>Priorytet *</label>
            <select name="waga" value={form.waga} onChange={handleChange}>
              <option>Bardzo Niski</option><option>Niski</option><option>Neutralny</option>
              <option>Wysoki</option><option>Krytyczny</option>
            </select>
          </div>

          {/* ——— PRAWA kolumna ——— */}
          <div className="column">
            <label>Klient</label>
            {clientsErr && <p className="error">{clientsErr}</p>}
            <select name="klient_id" value={form.klient_id} onChange={handleChange}>
              <option value="">— wybierz —</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>

            <label>Osoba zlecająca</label>
            <input type="text" name="osoba_zlecajaca" value={form.osoba_zlecajaca} onChange={handleChange} />

            <label>Odstęp generowania *</label>
            <select name="scheme" value={form.scheme} onChange={handleSchemeChange}>
              <option value="monthly">Miesięczne</option>
              <option value="quarterly">Kwartalne</option>
              <option value="halfyear">Półroczne</option>
              <option value="yearly">Roczne</option>
              <option value="everyNdays">Co N dni</option>
              <option value="custom">Niestandardowe</option>
            </select>

            {form.scheme==="everyNdays" && (
              <>
                <label>Co ile dni</label>
                <input type="number" min="1" name="every_n_days"
                       value={form.every_n_days} onChange={handleChange} />
              </>
            )}

            {form.scheme==="custom" && (
              <>
                <label>Dodaj datę</label>
                <div className="add-date-row">
                  <input type="date" value={customDateInput}
                         onChange={e=>setCustomDateInput(e.target.value)} />
                  <button type="button" onClick={addCustomDate}>+</button>
                </div>
                <div className="custom-dates-list">
                  {form.custom_dates.map(d=>(
                    <span key={d} onClick={()=>removeCustomDate(d)}>{d} ×</span>
                  ))}
                </div>
              </>
            )}

            <label>Start generowania *</label>
            <input type="date" name="start_date" required
                   value={form.start_date} onChange={onStartDateChange} />

            <label>Okres generowania (do) *</label>
            <input type="date" name="end_date"   required
                   value={form.end_date}   onChange={handleChange} />
          </div>

          <div className="footer-fixed">
            {error && <p className="error" style={{marginRight:"auto"}}>{error}</p>}
            <button type="submit" className="btn-lg blue">Zapisz</button>
            <button type="button" className="btn-lg grey"
                    onClick={() => { setEditingId(null); setView("menu"); }}>
              ⟵ Wróć
            </button>
          </div>
        </form>
      </>
    );
  })();

  /* ========== RENDER ========== */
  if(!visible) return null;
  return(
    <div className="modal-overlay large">
      <div className="system-modal">{content}</div>
    </div>
  );
}
